import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import type { AdminGatewayAccount, Gateway, GatewayAccountMode, TestBenchPreset } from '@/api/models';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { fetchAllAdminTenants } from '@/api/fetchAll';
import { payApiClient } from '@/api/client';
import { FilterableSelect } from '@/components/form/FilterableSelect';
import { tenantToOption } from '@/components/form/optionMappers';
import { PillTabs } from '@/components/shell/PillTabs';
import { TagsField } from '@/features/operations/filters/TagsField';
import { useApiResource } from '@/hooks/useApiResource';
import { useTestBenchAvailability } from '@/features/test-bench/useTestBenchAvailability';
import { accountFieldLabel, isAccountFieldVisible } from './domain';
import { useGatewayDescriptor } from './useGatewayDescriptors';

interface GatewayAccountCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (account: Pick<AdminGatewayAccount, 'id'>) => void;
}

type CredentialsSource = 'real' | 'preset';

const GATEWAY_VALUES: Gateway[] = ['redsys', 'stripe', 'adyen', 'payten', 'plexo'];
const MODE_VALUES: GatewayAccountMode[] = ['test', 'live'];

/**
 * `POST /v1/admin/gateway-accounts` (real-credentials source, unchanged) or,
 * when the test bench is available (D4), `POST
 * /v1/admin/test-bench/presets/{code}/link` (sandbox-preset source — same
 * endpoint "Vincular proveedor de pruebas" uses). This dialog never asks for
 * a credential value itself either way: the real-credentials flow leaves
 * `.../secrets` as a separate, dedicated write-only operation (see
 * `GatewayAccountSecretsWriteDialog`), and the preset flow writes the
 * credential server-side as part of linking.
 */
export function GatewayAccountCreateDialog({ open, onClose, onCreated }: GatewayAccountCreateDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const { available: testBenchAvailable } = useTestBenchAvailability();
  const [source, setSource] = useState<CredentialsSource>('real');
  const [tenantCode, setTenantCode] = useState('');
  const [gateway, setGateway] = useState<Gateway>('redsys');
  const [mode, setMode] = useState<GatewayAccountMode>('test');
  const [alias, setAlias] = useState('');
  // Whether the operator typed the alias themselves. While `false`, picking a
  // tenant (re)fills the alias with that tenant's name; clearing the field by
  // hand hands control back to the autofill.
  const [aliasEdited, setAliasEdited] = useState(false);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [externalAccountCode, setExternalAccountCode] = useState('');
  const [externalTerminalId, setExternalTerminalId] = useState('');
  const [externalEndpointPrefix, setExternalEndpointPrefix] = useState('');
  const [presetCode, setPresetCode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Mirrors `TestBenchPresetLinkDialog`'s own guard: MUI's `Dialog` mounts
  // its children even while `open` is `false`, so this fetcher only actually
  // calls out once both the dialog is open and the preset source is picked.
  // What the chosen gateway actually reads, published by the platform: the
  // inputs below follow it rather than offering every gateway's fields to every
  // gateway.
  const { descriptor } = useGatewayDescriptor(gateway, open && source === 'real');

  const tenants = useApiResource(
    () => (open ? fetchAllAdminTenants() : Promise.resolve([])),
    `gateway-account-create-tenants-${open}`,
  );

  const presets = useApiResource(
    () => (open && source === 'preset' ? payApiClient.getTestBenchPresets() : Promise.resolve([])),
    `gateway-account-create-presets-${open}-${source}`,
  );

  useEffect(() => {
    if (!presetCode && presets.data && presets.data.length > 0) {
      setPresetCode(presets.data[0]?.code ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets.data]);

  function resetAndClose() {
    setSource('real');
    setTenantCode('');
    setGateway('redsys');
    setMode('test');
    setAlias('');
    setAliasEdited(false);
    setCapabilities([]);
    setExternalAccountCode('');
    setExternalTerminalId('');
    setPresetCode('');
    setAccessDenied(false);
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      if (source === 'preset') {
        const response = await payApiClient.linkTestBenchPreset(presetCode, {
          tenantCode: tenantCode.trim(),
          alias: alias.trim() || undefined,
        });
        notify(translate('pay.testBench.preset.success'), { type: 'success' });
        resetAndClose();
        onCreated({ id: response.gatewayAccountId });
      } else {
        const account = await payApiClient.createAdminGatewayAccount({
          tenantCode: tenantCode.trim(),
          gateway,
          mode,
          alias: alias.trim(),
          capabilities,
          externalAccountCode: externalAccountCode.trim() || undefined,
          externalTerminalId: externalTerminalId.trim() || undefined,
          externalEndpointPrefix: externalEndpointPrefix.trim() || undefined,
        });
        notify(translate('pay.gatewayAccounts.create.success'), { type: 'success' });
        resetAndClose();
        onCreated(account);
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const presetOptions: TestBenchPreset[] = presets.data ?? [];
  const canSubmit =
    tenantCode.trim().length > 0 &&
    !submitting &&
    (source === 'preset' ? presetCode.trim().length > 0 : alias.trim().length > 0);

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.gatewayAccounts.create.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {testBenchAvailable && (
              <Stack spacing={0.75}>
                <Typography variant="caption" color="text.secondary">
                  {translate('pay.gatewayAccounts.create.sourceLabel')}
                </Typography>
                <PillTabs
                  value={source}
                  onChange={(next) => setSource(next as CredentialsSource)}
                  options={[
                    { value: 'real', label: translate('pay.gatewayAccounts.create.sourceReal') },
                    { value: 'preset', label: translate('pay.gatewayAccounts.create.sourcePreset') },
                  ]}
                />
              </Stack>
            )}
            <FilterableSelect
              autoFocus
              required
              label={translate('pay.gatewayAccounts.create.fieldTenant')}
              helperText={translate('pay.gatewayAccounts.create.fieldTenantHelp')}
              options={(tenants.data ?? []).map(tenantToOption)}
              loading={tenants.loading}
              value={tenantCode}
              onChange={(code, option) => {
                setTenantCode(code);
                // The tenant's name is the natural alias — proposed, never imposed:
                // an alias the operator already typed stays untouched.
                if (source === 'real' && !aliasEdited) {
                  setAlias(option?.label ?? '');
                }
              }}
            />
            {source === 'preset' ? (
              <>
                <FilterableSelect
                  disableClearable
                  label={translate('pay.testBench.preset.fieldPreset')}
                  options={presetOptions.map((preset) => ({ value: preset.code, label: preset.name, secondary: preset.code }))}
                  loading={presets.loading}
                  value={presetCode}
                  onChange={(code) => {
                    if (code) setPresetCode(code);
                  }}
                  helperText={presetOptions.length === 0 ? translate('pay.testBench.preset.noPresets') : undefined}
                />
                <TextField
                  size="small"
                  label={translate('pay.testBench.preset.fieldAlias')}
                  helperText={translate('pay.testBench.preset.fieldAliasHelp')}
                  value={alias}
                  onChange={(event) => setAlias(event.target.value)}
                  fullWidth
                />
              </>
            ) : (
              <>
                <FilterableSelect
                  disableClearable
                  label={translate('pay.gatewayAccounts.create.fieldGateway')}
                  options={GATEWAY_VALUES.map((value) => ({
                    value,
                    label: translate(`pay.gatewayAccounts.gateway.${value}`),
                  }))}
                  value={gateway}
                  onChange={(value) => {
                    if (value) setGateway(value as Gateway);
                  }}
                />
                <TextField
                  select
                  size="small"
                  label={translate('pay.gatewayAccounts.create.fieldMode')}
                  value={mode}
                  onChange={(event) => setMode(event.target.value as GatewayAccountMode)}
                  fullWidth
                >
                  {MODE_VALUES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {translate(`pay.gatewayAccounts.mode.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label={translate('pay.gatewayAccounts.create.fieldAlias')}
                  value={alias}
                  onChange={(event) => {
                    setAlias(event.target.value);
                    // An emptied field means "propose again"; anything typed is the operator's own.
                    setAliasEdited(event.target.value.trim().length > 0);
                  }}
                  fullWidth
                />
                <TagsField
                  label={translate('pay.gatewayAccounts.create.fieldCapabilities')}
                  helperText={translate('pay.gatewayAccounts.create.fieldCapabilitiesHelp')}
                  values={capabilities}
                  onChange={setCapabilities}
                />
                {isAccountFieldVisible(descriptor?.accountFields.externalAccountCode, externalAccountCode) && (
                  <TextField
                    size="small"
                    label={accountFieldLabel(translate, gateway, 'externalAccountCode', translate('pay.gatewayAccounts.create.fieldExternalAccountCode'))}
                    value={externalAccountCode}
                    onChange={(event) => setExternalAccountCode(event.target.value)}
                    fullWidth
                  />
                )}
                {isAccountFieldVisible(descriptor?.accountFields.externalTerminalId, externalTerminalId) && (
                  <TextField
                    size="small"
                    label={`${accountFieldLabel(translate, gateway, 'externalTerminalId', translate('pay.gatewayAccounts.create.fieldExternalTerminalId'))} ${translate('pay.gatewayAccounts.create.optionalSuffix')}`}
                    value={externalTerminalId}
                    onChange={(event) => setExternalTerminalId(event.target.value)}
                    fullWidth
                  />
                )}
                {isAccountFieldVisible(descriptor?.accountFields.externalEndpointPrefix, externalEndpointPrefix) && (
                  <TextField
                    size="small"
                    label={accountFieldLabel(translate, gateway, 'externalEndpointPrefix', translate('pay.gatewayAccounts.create.fieldExternalEndpointPrefix'))}
                    helperText={translate('pay.gatewayAccounts.create.fieldExternalEndpointPrefixHelp')}
                    value={externalEndpointPrefix}
                    onChange={(event) => setExternalEndpointPrefix(event.target.value)}
                    fullWidth
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>{translate('pay.gatewayAccounts.create.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {source === 'preset' ? translate('pay.testBench.preset.submit') : translate('pay.gatewayAccounts.create.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
